import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  BarChart3, 
  PieChart as PieChartIcon,
  TrendingUp,
  Wallet,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  AreaChart,
  Area
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ReportsPage() {
  const { household } = useAuth();
  const [loading, setLoading] = useState(true);
  const [spendingReport, setSpendingReport] = useState(null);
  const [incomeExpensesReport, setIncomeExpensesReport] = useState(null);
  const [netWorthReport, setNetWorthReport] = useState(null);

  useEffect(() => {
    if (!household) return;
    fetchData();
  }, [household]);

  const fetchData = async () => {
    try {
      const [spendingRes, incomeRes, networthRes] = await Promise.all([
        fetch(`${API_URL}/api/households/${household.household_id}/reports/spending?months=1`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/reports/income-expenses?months=6`, { credentials: 'include' }),
        fetch(`${API_URL}/api/households/${household.household_id}/reports/net-worth`, { credentials: 'include' })
      ]);

      if (spendingRes.ok) setSpendingReport(await spendingRes.json());
      if (incomeRes.ok) setIncomeExpensesReport(await incomeRes.json());
      if (networthRes.ok) setNetWorthReport(await networthRes.json());
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const currency = household?.currency || 'NZD';
    return new Intl.NumberFormat('en-NZ', { 
      style: 'currency', 
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(amount);
  };

  const COLORS = ['#10B981', '#F59E0B', '#3B82F6', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316'];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 lg:p-8" data-testid="reports-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Financial insights and trends</p>
        </div>

        <Tabs defaultValue="spending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="spending" data-testid="tab-spending">
              <PieChartIcon className="w-4 h-4 mr-2" />
              Spending
            </TabsTrigger>
            <TabsTrigger value="income-expenses" data-testid="tab-income-expenses">
              <BarChart3 className="w-4 h-4 mr-2" />
              Income vs Expenses
            </TabsTrigger>
            <TabsTrigger value="net-worth" data-testid="tab-net-worth">
              <TrendingUp className="w-4 h-4 mr-2" />
              Net Worth
            </TabsTrigger>
          </TabsList>

          {/* Spending Report */}
          <TabsContent value="spending" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Summary Card */}
              <div className="card-dashboard">
                <h3 className="font-heading font-semibold mb-4">This Month</h3>
                <p className="font-heading text-4xl font-bold text-destructive mb-2" data-testid="total-spending">
                  {formatCurrency(spendingReport?.total_spending || 0)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {spendingReport?.transaction_count || 0} transactions
                </p>
              </div>

              {/* Pie Chart */}
              <div className="lg:col-span-2 card-dashboard">
                <h3 className="font-heading font-semibold mb-4">Spending by Category</h3>
                
                {spendingReport?.by_category?.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={spendingReport.by_category.slice(0, 8)}
                          cx="50%"
                          cy="50%"
                          outerRadius={120}
                          innerRadius={60}
                          dataKey="total"
                          nameKey="name"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {spendingReport.by_category.slice(0, 8).map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload?.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-card px-4 py-3 rounded-lg shadow-lg border border-border">
                                  <p className="font-medium">{data.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {formatCurrency(data.total)} ({data.count} transactions)
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-muted-foreground">
                    No spending data for this period
                  </div>
                )}
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-4">Category Breakdown</h3>
              
              <div className="space-y-3">
                {(spendingReport?.by_category || []).map((category, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: category.color || COLORS[index % COLORS.length] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium truncate">{category.name}</span>
                        <span className="font-medium tabular-nums">{formatCurrency(category.total)}</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${(category.total / (spendingReport?.total_spending || 1)) * 100}%`,
                            backgroundColor: category.color || COLORS[index % COLORS.length]
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground w-16 text-right">
                      {category.count} txns
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Income vs Expenses Report */}
          <TabsContent value="income-expenses" className="space-y-6">
            <div className="card-dashboard">
              <h3 className="font-heading font-semibold mb-6">Monthly Comparison (Last 6 Months)</h3>
              
              {incomeExpensesReport?.monthly_data?.length > 0 ? (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={incomeExpensesReport.monthly_data}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (active && payload?.length) {
                            return (
                              <div className="bg-card px-4 py-3 rounded-lg shadow-lg border border-border">
                                <p className="font-medium mb-2">{label}</p>
                                {payload.map((entry, index) => (
                                  <p key={index} className="text-sm" style={{ color: entry.color }}>
                                    {entry.name}: {formatCurrency(entry.value)}
                                  </p>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend />
                      <Bar dataKey="income" name="Income" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="expenses" name="Expenses" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-96 flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </div>

            {/* Monthly Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(incomeExpensesReport?.monthly_data || []).slice(-3).reverse().map((month, index) => (
                <div key={index} className="card-dashboard">
                  <h4 className="font-medium text-muted-foreground mb-4">{month.month}</h4>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ArrowDownRight className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm">Income</span>
                      </div>
                      <span className="font-medium text-emerald-600">{formatCurrency(month.income)}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-amber-500" />
                        <span className="text-sm">Expenses</span>
                      </div>
                      <span className="font-medium text-amber-600">{formatCurrency(month.expenses)}</span>
                    </div>
                    
                    <div className="pt-3 border-t border-border">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Net</span>
                        <span className={`font-heading font-bold ${month.net >= 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                          {month.net >= 0 ? '+' : ''}{formatCurrency(month.net)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Net Worth Report */}
          <TabsContent value="net-worth" className="space-y-6">
            <div className="card-dashboard">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading font-semibold">Net Worth Over Time</h3>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={async () => {
                    await fetch(`${API_URL}/api/households/${household.household_id}/reports/net-worth/snapshot`, {
                      method: 'POST',
                      credentials: 'include'
                    });
                    fetchData();
                  }}
                  data-testid="snapshot-btn"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Take Snapshot
                </Button>
              </div>
              
              {netWorthReport?.snapshots?.length > 0 ? (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={netWorthReport.snapshots}>
                      <defs>
                        <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-NZ', { month: 'short' })}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(value) => formatCurrency(value)}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload?.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-card px-4 py-3 rounded-lg shadow-lg border border-border">
                                <p className="font-medium mb-2">
                                  {new Date(data.date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                                <div className="space-y-1 text-sm">
                                  <p>Assets: <span className="text-emerald-600">{formatCurrency(data.assets_total)}</span></p>
                                  <p>Liabilities: <span className="text-destructive">{formatCurrency(data.liabilities_total)}</span></p>
                                  <p className="font-medium pt-1 border-t border-border">
                                    Net Worth: <span className={data.net_worth >= 0 ? 'text-emerald-600' : ''}>{formatCurrency(data.net_worth)}</span>
                                  </p>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="net_worth" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#netWorthGradient)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-96 flex flex-col items-center justify-center text-muted-foreground">
                  <Wallet className="w-12 h-12 mb-4" />
                  <p>No snapshots yet</p>
                  <p className="text-sm mt-1">Take your first snapshot to start tracking</p>
                </div>
              )}
            </div>

            {/* Latest Snapshot Details */}
            {netWorthReport?.snapshots?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
                      <ArrowDownRight className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="font-medium">Total Assets</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-emerald-600">
                    {formatCurrency(netWorthReport.snapshots[netWorthReport.snapshots.length - 1]?.assets_total || 0)}
                  </p>
                </div>
                
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="font-medium">Total Liabilities</span>
                  </div>
                  <p className="font-heading text-3xl font-bold text-destructive">
                    {formatCurrency(netWorthReport.snapshots[netWorthReport.snapshots.length - 1]?.liabilities_total || 0)}
                  </p>
                </div>
                
                <div className="card-dashboard">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="font-medium">Net Worth</span>
                  </div>
                  <p className="font-heading text-3xl font-bold">
                    {formatCurrency(netWorthReport.snapshots[netWorthReport.snapshots.length - 1]?.net_worth || 0)}
                  </p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
